/**
 * The five base classes (docs/design/classes/advancement.md, "Base classes (build now): levels 1–7"):
 * Warrior, Rogue, Mage, Acolyte, Ranger. One new ability per level, 1–7, on keys 1–7 (keys 1–4 are
 * `binds`, 5–7 `extraBinds`), paced like WoW's first five: a core attack, a second button that
 * makes a decision, control, mobility or defence, then the "now I feel strong" piece; 6 and 7 are
 * utility. Dodge is everyone's from level 1. They replace the four M+ kits (warden, lightbinder,
 * blademaster, stormcaller) as the playable classes; those stay for the legacy M+ harness only.
 *
 * Naming rule: no ability, buff, mechanic or tooltip name copies a World of Warcraft name (only the
 * class names are genre staples); bases.test.ts holds the denylist. Art reuse: `ui.animAs` points
 * each ability at the old kit's look (Warden → Warrior, Lightbinder → Acolyte, Blademaster → Rogue,
 * Stormcaller → Mage); the web maps icons, VFX and clips through it (BASE_VISUAL_ALIAS). The Ranger
 * has its own arrow VFX. `ui.hint` is the one-line "how to use" on the level-up banner.
 *
 * Budget (level-10 units, power 1000): a DPS base does ≈ 3.5–4k single-target DPS, the Warrior a
 * little less with its sustain, the Acolyte ≈ 3k. Time to level 10 with Auto is kept within ±15%
 * across the five (harness grind, grind.test.ts).
 */
import type { AbilityDef, AuraDef, ClassDef, Condition, EffectSpec } from '@keystone/sim';
import { ABILITY, BASE_ABILITY, BASE_AURA, BASE_CLASS } from '../ids';
import { PLAYER_HP, baseStats } from '../balance';
import { OFF_GCD, SPELL_RANGE, aroundSelf, dodge, interrupt, selfBuff } from './common';
import { RANGER_SPEC_NAME } from './specs/ranger'; // +ranger-specs: spec display names

const WA = BASE_ABILITY.warrior;
const RO = BASE_ABILITY.rogue;
const MA = BASE_ABILITY.mage;
const AC = BASE_ABILITY.acolyte;
const RA = BASE_ABILITY.ranger;

const alliesAround = (radius: number) => ({ anchor: 'caster' as const, shape: { type: 'circle' as const, radius }, affects: 'allies' as const });
const OUT_OF_COMBAT: Condition = { type: 'not', cond: { type: 'selfInCombat' } };

/** An out-of-combat toggle: press to put the buff on, press again to take it off. */
function toggle(aura: AuraDef): EffectSpec {
  return { type: 'conditional', cond: { type: 'selfHasAura', auraId: aura.id }, then: [{ type: 'removeAura', to: 'self', auraId: aura.id }], else: [{ type: 'applyAura', to: 'self', aura }] };
}

// ============================================================================
// Warrior: tank (shield). Grit comes from swings and hits taken; Breaker spends it.
// ============================================================================

/** Bloodprize's window: opened by kill credit (the hidden Kill Sense passive), glows the button. */
const freshKill: AuraDef = {
  id: BASE_AURA.freshKill,
  name: 'Fresh Kill',
  kind: 'buff',
  durationMs: 20_000,
  stacking: 'refresh',
  flags: { hidden: true },
  ui: { proc: true, glow: { abilityIds: [WA.bloodprize] } },
};

const breaker: AbilityDef = {
  id: WA.breaker,
  name: 'Breaker',
  school: 'physical',
  description: 'Spend 20 Grit: a crushing overhead blow. Grit builds from your swings and from hits you take.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cost: [{ resource: 'grit', amount: 20 }],
  flatThreat: 1200,
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 4.8, variance: 0.05 } }],
  ui: {
    tags: ['spender'],
    color: '#4a7bd1',
    animAs: ABILITY.warden.bulwarkStrike,
    desc: 'Spend 20 Grit on a crushing overhead blow for {damage} Physical damage. You gain Grit from every swing that lands and from damage you take.',
    hint: 'Press 1 to open (you rest at 20 Grit), then again every two swings.',
  },
};

const headlong: AbilityDef = {
  id: WA.headlong,
  name: 'Headlong',
  school: 'physical',
  description: 'Charge an enemy up to 25 yd away, root it for 1 s and gain 15 Grit.',
  targeting: 'enemy',
  range: 25,
  requiresFacing: true,
  requiresMobility: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  gain: [{ resource: 'grit', amount: 15 }],
  flatThreat: 600,
  effects: [
    { type: 'dash', mode: 'toTarget', distance: 25, speed: 42 },
    { type: 'damage', school: 'physical', amount: { coef: 0.8, variance: 0.05 } },
    { type: 'applyAura', aura: { id: BASE_AURA.headlongRoot, name: 'Bowled Over', kind: 'debuff', durationMs: 1000, stacking: 'refresh', cc: 'root' } },
  ],
  ui: {
    tags: ['movement'],
    color: '#8fb0e6',
    animAs: ABILITY.blademaster.charge,
    desc: 'Charge an enemy up to 25 yd away. It takes {damage} Physical damage and is rooted for 1 sec, and you gain 15 Grit. Off the global cooldown.',
    hint: 'Press 2 on a target up to 25 yd away to charge in and open with 15 Grit.',
  },
};

const wideArc: AbilityDef = {
  id: WA.wideArc,
  name: 'Wide Arc',
  school: 'physical',
  description: 'Sweep every enemy within 8 yd. Double threat. Generates 10 Grit.',
  targeting: 'none',
  cooldown: { durationMs: 6000 },
  gain: [{ resource: 'grit', amount: 10 }],
  flatThreat: 600,
  effects: [{ type: 'damage', school: 'physical', to: 'area', area: aroundSelf(8, 10), amount: { coef: 2.4, variance: 0.05 }, threatMult: 2, isAoE: true }],
  ui: {
    tags: ['aoe'],
    color: '#9fb4d8',
    animAs: ABILITY.warden.cleaveStrike,
    desc: 'Sweep your blade through every enemy within 8 yd for {damage} Physical damage, with double threat. Generates 10 Grit.',
    hint: 'Press 3 with enemies around you: hits them all and holds their attention.',
  },
};

const riveBleed: AuraDef = {
  id: BASE_AURA.rive,
  name: 'Rive',
  kind: 'debuff',
  durationMs: 12_000,
  stacking: 'refresh',
  pandemic: true,
  periodic: { intervalMs: 3000, effects: [{ type: 'damage', school: 'physical', amount: { coef: 1.0 }, avoidable: false }] },
  mods: [{ stat: 'moveSpeedPct', value: -0.3 }],
};

const rive: AbilityDef = {
  id: WA.rive,
  name: 'Rive',
  school: 'physical',
  description: 'Spend 10 Grit: tear a wound that bleeds for 12 s and slows the target by 30%.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cost: [{ resource: 'grit', amount: 10 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.6, variance: 0.05 } }, { type: 'applyAura', aura: riveBleed }],
  ui: {
    tags: ['spender'],
    color: '#c0392b',
    animAs: ABILITY.blademaster.rend,
    desc: 'Spend 10 Grit to tear a wound for {damage} Physical damage. It bleeds for {damage2} every 3 sec for 12 sec and slows the target by 30%.',
    hint: 'Press 4 to keep a bleed on your target; it also slows runners by 30%.',
  },
};

const bloodprize: AbilityDef = {
  id: WA.bloodprize,
  name: 'Bloodprize',
  school: 'physical',
  description: 'Usable for 20 s after a kill you get credit for: a strike that heals you for 20% of your maximum health.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  usableIf: { type: 'selfHasAura', auraId: BASE_AURA.freshKill },
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 4.2, variance: 0.05 } },
    { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.2 } },
    { type: 'removeAura', to: 'self', auraId: BASE_AURA.freshKill },
  ],
  ui: {
    tags: ['heal'],
    color: '#b03a48',
    animAs: ABILITY.warden.shieldSlam,
    desc: 'Usable for 20 sec after an enemy you fought dies. Strike for {damage} Physical damage and heal yourself for 20% of your maximum health. The button glows while it is ready.',
    hint: 'After a kill key 5 glows: hit the next enemy within 20 s to heal 20%.',
  },
};

const shieldCheck: AbilityDef = {
  id: WA.shieldCheck,
  name: 'Shield Check',
  school: 'physical',
  description: "Check the target with your shield, interrupting its cast and locking that school for 3 s.",
  targeting: 'enemy',
  melee: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15000 },
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.6 } }, interrupt(3000)],
  ui: {
    tags: ['interrupt'],
    color: '#6d8fd6',
    animAs: ABILITY.warden.shieldBash,
    hint: 'Press 6 while an enemy is casting to stop the spell.',
  },
};

const ironBreath: AbilityDef = {
  id: WA.ironBreath,
  name: 'Iron Breath',
  school: 'physical',
  description: 'Steady yourself: heal 25% of your maximum health and take 20% less damage for 6 s.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 40000 },
  effects: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.25 } }, selfBuff(BASE_AURA.ironBreath, 'Iron Breath', 6000, [{ stat: 'damageTakenPct', value: -0.2 }])],
  ui: {
    tags: ['defensive', 'majorDefensive'],
    color: '#9fb4d8',
    animAs: ABILITY.warden.lastStand,
    hint: 'Press 7 when you are low: heals 25% and cuts damage taken for 6 s.',
  },
};

export const warrior: ClassDef = {
  id: BASE_CLASS.warrior,
  name: 'Warrior',
  role: 'tank',
  // Grit rests at 20 out of combat (Breaker always opens) and builds from swings and hits taken.
  resources: [{ id: 'grit', name: 'Grit', max: 100, start: 20, regenPerSecOoc: 5, oocSettle: 20, gainOn: [{ on: 'damageTaken', amount: 1, perPctMaxHp: true }, { on: 'meleeSwingLanded', amount: 10 }] }],
  binds: { '1': WA.breaker, '2': WA.headlong, '3': WA.wideArc, '4': WA.rive },
  extraBinds: { '5': WA.bloodprize, '6': WA.shieldCheck, '7': WA.ironBreath },
  abilities: [breaker, headlong, wideArc, rive, bloodprize, shieldCheck, ironBreath, dodge],
  extraAbilities: [ABILITY.common.dodge],
  baseStats: baseStats({ armor: 0.3, dodge: 0.1, parry: 0.1, block: 0.08, blockAmount: 0.3 }),
  maxHp: PLAYER_HP.tank,
  moveSpeed: 7,
  melee: { damage: { coef: 1.8, variance: 0.05 }, speedMs: 2600, school: 'physical' },
  passives: [
    {
      id: BASE_AURA.warriorThreat,
      name: 'Steadfast Vow',
      kind: 'buff',
      durationMs: 0,
      stacking: 'unique',
      mods: [{ stat: 'threatPct', value: 3 }],
      flags: { hidden: true, persistThroughDeath: true },
    },
    {
      id: BASE_AURA.killSense,
      name: 'Kill Sense',
      kind: 'buff',
      durationMs: 0,
      stacking: 'unique',
      flags: { hidden: true, persistThroughDeath: true },
      procs: [{ on: 'killCredit', effects: [{ type: 'applyAura', to: 'self', aura: freshKill }] }],
    },
  ],
  color: '#4a7bd1',
  unlocks: { [ABILITY.common.dodge]: 1, [WA.breaker]: 1, [WA.headlong]: 2, [WA.wideArc]: 3, [WA.rive]: 4, [WA.bloodprize]: 5, [WA.shieldCheck]: 6, [WA.ironBreath]: 7 },
};

// ============================================================================
// Rogue: melee damage. Tempo feeds Nick and Bladefan; Edge feeds Finale. Lurk opens fights.
// ============================================================================

const nick: AbilityDef = {
  id: RO.nick,
  name: 'Nick',
  school: 'physical',
  description: 'A quick cut. Costs 25 Tempo, builds 1 Edge.',
  targeting: 'enemy',
  melee: true,
  gcdMs: 1000,
  cost: [{ resource: 'tempo', amount: 25 }],
  gain: [{ resource: 'edge', amount: 1 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 2.1, variance: 0.05 } }],
  ui: { tags: ['builder'], color: '#c9d3e6', animAs: ABILITY.blademaster.overpower, hint: 'Press 1 to cut: each Nick costs Tempo and builds 1 Edge.' },
};

const finale: AbilityDef = {
  id: RO.finale,
  name: 'Finale',
  school: 'physical',
  description: 'Spend 4 Edge: a flurry that ends in a deep cut.',
  targeting: 'enemy',
  melee: true,
  gcdMs: 1000,
  requiresFacing: true,
  cost: [{ resource: 'edge', amount: 4 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 8.4, variance: 0.05 } }],
  ui: { tags: ['spender'], color: '#e04848', animAs: ABILITY.blademaster.execute, hint: 'Press 2 at 4 Edge (four Nicks) for a big finishing cut.' },
};

const reeling: AuraDef = { id: BASE_AURA.reeling, name: 'Reeling', kind: 'debuff', durationMs: 2000, stacking: 'refresh', cc: 'stun' };

const dirtyOpener: AbilityDef = {
  id: RO.dirtyOpener,
  name: 'Dirty Opener',
  school: 'physical',
  description: 'From Lurk: a low blow that stuns the target for 2 s and builds 2 Edge.',
  targeting: 'enemy',
  melee: true,
  gcdMs: 1000,
  gain: [{ resource: 'edge', amount: 2 }],
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 2.8, variance: 0.05 }, avoidable: false },
    { type: 'applyAura', aura: reeling },
    { type: 'removeAura', to: 'self', auraId: BASE_AURA.lurk },
  ],
  ui: { tags: ['builder'], color: '#8e7cc3', animAs: ABILITY.blademaster.overpower, desc: 'A low blow from the shadows for {damage} Physical damage. Stuns the target for 2 sec and builds 2 Edge. Ends Lurk.' },
};

const lurkBuff: AuraDef = {
  id: BASE_AURA.lurk,
  name: 'Lurking',
  kind: 'buff',
  durationMs: 0,
  stacking: 'refresh',
  flags: { stealth: true, removeInCombat: true, breakOnDamage: true },
  procs: [{ on: 'damageDealt', effects: [{ type: 'removeAura', to: 'self', auraId: BASE_AURA.lurk }] }],
  overrides: [{ abilityId: RO.nick, with: RO.dirtyOpener }],
};

const lurk: AbilityDef = {
  id: RO.lurk,
  name: 'Lurk',
  school: 'physical',
  description: 'Out of combat: slip into the shadows. Enemies only notice you up close, and Nick becomes Dirty Opener.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 2000 },
  usableIf: { type: 'or', any: [OUT_OF_COMBAT, { type: 'selfHasAura', auraId: BASE_AURA.lurk }] },
  effects: [toggle(lurkBuff)],
  ui: {
    tags: ['cooldown'],
    color: '#6c5b8f',
    animAs: ABILITY.blademaster.dieByTheSword,
    desc: 'Out of combat only. Slip into the shadows: enemies notice you only from 3 yd away, and Nick becomes Dirty Opener (2 sec stun, +2 Edge). Breaks when you deal or take damage or a fight starts. Press again to step out.',
    hint: 'Press 3 out of combat to sneak up, then 1 opens with a 2 s stun.',
  },
};

const slip: AbilityDef = {
  id: RO.slip,
  name: 'Slip',
  school: 'physical',
  description: "Vanish and reappear behind the target (up to 25 yd), building 1 Edge.",
  targeting: 'enemy',
  range: 25,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15000 },
  requiresMobility: true,
  gain: [{ resource: 'edge', amount: 1 }],
  effects: [{ type: 'dash', mode: 'behindTarget', distance: 25 }],
  ui: { tags: ['movement'], color: '#8fa0c8', animAs: ABILITY.blademaster.charge, hint: 'Press 4 to step behind a target up to 25 yd away (+1 Edge).' },
};

const bladefan: AbilityDef = {
  id: RO.bladefan,
  name: 'Bladefan',
  school: 'physical',
  description: 'Fan your blades out at every enemy within 7 yd. Costs 35 Tempo, builds 1 Edge.',
  targeting: 'none',
  gcdMs: 1000,
  cost: [{ resource: 'tempo', amount: 35 }],
  gain: [{ resource: 'edge', amount: 1 }],
  effects: [{ type: 'damage', school: 'physical', to: 'area', area: aroundSelf(7, 8), amount: { coef: 1.9, variance: 0.05 }, isAoE: true }],
  ui: { tags: ['aoe', 'builder'], color: '#c9d3e6', animAs: ABILITY.blademaster.cleave, hint: 'Press 5 in a pack: cuts every enemy within 7 yd.' },
};

const ashveil: AbilityDef = {
  id: RO.ashveil,
  name: 'Ashveil',
  school: 'physical',
  description: 'Drop a smoke veil: dodge most attacks and take 30% less damage for 5 s.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 45000 },
  effects: [
    selfBuff(BASE_AURA.ashveil, 'Ashveil', 5000, [
      { stat: 'dodge', value: 0.75 },
      { stat: 'damageTakenPct', value: -0.3 },
    ]),
  ],
  ui: { tags: ['defensive'], color: '#7c7c8c', animAs: ABILITY.blademaster.dieByTheSword, hint: 'Press 6 when hurt: smoke makes most attacks miss for 5 s.' },
};

const throatjab: AbilityDef = {
  id: RO.throatjab,
  name: 'Throatjab',
  school: 'physical',
  description: 'Jab the target in the throat, interrupting its cast and locking that school for 3 s.',
  targeting: 'enemy',
  melee: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15000 },
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.5 } }, interrupt(3000)],
  ui: { tags: ['interrupt'], color: '#d9a066', animAs: ABILITY.blademaster.pummel, hint: 'Press 7 while an enemy is casting to stop the spell.' },
};

export const rogue: ClassDef = {
  id: BASE_CLASS.rogue,
  name: 'Rogue',
  role: 'dps',
  resources: [
    { id: 'tempo', name: 'Tempo', max: 100, start: 100, regenPerSec: 14 },
    { id: 'edge', name: 'Edge', max: 5, start: 0 },
  ],
  binds: { '1': RO.nick, '2': RO.finale, '3': RO.lurk, '4': RO.slip },
  extraBinds: { '5': RO.bladefan, '6': RO.ashveil, '7': RO.throatjab },
  abilities: [nick, finale, lurk, slip, bladefan, ashveil, throatjab, dirtyOpener, dodge],
  extraAbilities: [ABILITY.common.dodge, RO.dirtyOpener],
  baseStats: baseStats(),
  maxHp: PLAYER_HP.melee,
  moveSpeed: 7,
  // +autoattack: dual daggers, 1.8 s each; the off hand runs its own timer (half a swing behind) for 50%.
  melee: { damage: { coef: 1.3, variance: 0.05 }, speedMs: 1800, school: 'physical', offhand: { speedMs: 1800, mult: 0.5 } },
  color: '#c9475f',
  unlocks: { [ABILITY.common.dodge]: 1, [RO.nick]: 1, [RO.finale]: 2, [RO.lurk]: 3, [RO.slip]: 4, [RO.bladefan]: 5, [RO.ashveil]: 6, [RO.throatjab]: 7 },
};

// ============================================================================
// Mage: ranged damage. Mana Dart builds Arcana; Arcane Lance spends it. Hoarbind roots.
// ============================================================================

const manaDart: AbilityDef = {
  id: MA.manaDart,
  name: 'Mana Dart',
  school: 'arcane',
  description: 'A quick bolt of raw mana. Builds 1 Arcana.',
  targeting: 'enemy',
  range: SPELL_RANGE,
  requiresFacing: true,
  cast: { type: 'cast', timeMs: 1500 },
  cost: [{ resource: 'mana', amount: 150 }],
  gain: [{ resource: 'arcana', amount: 1 }],
  effects: [{ type: 'damage', school: 'arcane', amount: { coef: 4.9, variance: 0.05 } }],
  ui: { tags: ['builder'], color: '#9b8cff', animAs: ABILITY.stormcaller.lightningBolt, hint: 'Press 1 and stand still for 1.5 s: a bolt that builds Arcana.' },
};

const arcaneLance: AbilityDef = {
  id: MA.arcaneLance,
  name: 'Arcane Lance',
  school: 'arcane',
  description: 'Spend 3 Arcana: an instant lance of arcane force.',
  targeting: 'enemy',
  range: SPELL_RANGE,
  requiresFacing: true,
  cost: [{ resource: 'arcana', amount: 3 }],
  effects: [{ type: 'damage', school: 'arcane', amount: { coef: 9.5, variance: 0.05 } }],
  ui: { tags: ['spender'], color: '#c0a8ff', animAs: ABILITY.stormcaller.earthShock, hint: 'Press 2 at 3 Arcana for an instant heavy hit, even on the move.' },
};

const hoarbound: AuraDef = { id: BASE_AURA.hoarbound, name: 'Hoarbound', kind: 'debuff', durationMs: 4000, stacking: 'refresh', cc: 'root' };

const hoarbind: AbilityDef = {
  id: MA.hoarbind,
  name: 'Hoarbind',
  school: 'frost',
  description: 'A ring of frost bursts out from you: enemies within 10 yd take frost damage and are rooted for 4 s.',
  targeting: 'none',
  cooldown: { durationMs: 20_000 },
  cost: [{ resource: 'mana', amount: 200 }],
  effects: [
    { type: 'damage', school: 'frost', to: 'area', area: aroundSelf(10, 10), amount: { coef: 0.6, variance: 0.05 }, isAoE: true },
    { type: 'applyAura', to: 'area', area: aroundSelf(10, 10), aura: hoarbound },
  ],
  ui: {
    tags: ['cooldown', 'aoe'],
    color: '#8fd3ff',
    animAs: ABILITY.stormcaller.earthquake,
    desc: 'A ring of frost bursts out from you: every enemy within 10 yd takes {damage} Frost damage and is rooted for 4 sec.',
    hint: 'Press 3 when enemies reach you: roots them for 4 s so you can step away.',
  },
};

const foldStep: AbilityDef = {
  id: MA.foldStep,
  name: 'Fold Step',
  school: 'arcane',
  description: 'Fold space and step 15 yd forward instantly.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15000 },
  requiresMobility: true,
  usableWhileCasting: true,
  effects: [{ type: 'dash', mode: 'forward', distance: 15 }],
  ui: { tags: ['movement'], color: '#b7a8ff', animAs: ABILITY.common.dodge, hint: 'Press 4 to blink 15 yd the way you face: out of a root, out of melee.' },
};

const flareBurst: AbilityDef = {
  id: MA.flareBurst,
  name: 'Flare Burst',
  school: 'fire',
  description: 'Detonate a flare at the target spot, burning every enemy within 8 yd.',
  targeting: 'ground',
  range: SPELL_RANGE,
  canCastWhileMoving: true,
  cooldown: { durationMs: 8000 },
  cost: [{ resource: 'mana', amount: 400 }],
  effects: [{ type: 'damage', school: 'fire', to: 'area', area: { anchor: 'point', shape: { type: 'circle', radius: 8 }, affects: 'enemies', maxTargets: 10 }, amount: { coef: 3.2, variance: 0.05 }, isAoE: true }],
  ui: { tags: ['aoe'], color: '#ff8a4a', animAs: ABILITY.stormcaller.lavaBurst, hint: 'Press 5 on a pack: a fire burst hits everything within 8 yd.' },
};

const glyphShield: AbilityDef = {
  id: MA.glyphShield,
  name: 'Glyph Shield',
  school: 'arcane',
  description: 'Wrap yourself in glyphs that absorb damage equal to 30% of your maximum health for 10 s.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 30000 },
  effects: [{ type: 'absorb', to: 'self', auraId: BASE_AURA.glyphShield, amount: { pctSourceMaxHp: 0.3 } }],
  ui: { tags: ['defensive'], color: '#a29bfe', animAs: ABILITY.stormcaller.astralShift, hint: 'Press 6 before a big hit: a shield soaks 30% of your health.' },
};

/**
 * Wandering Star: a slow orb of starlight that drifts ~30 yd over 3 s (a travelling zone, moved
 * deterministically by the sim), arcing into every enemy within 4 yd of it every 0.5 s, then
 * bursting at the end of its path.
 */
const wanderingStar: AbilityDef = {
  id: MA.wanderingStar,
  name: 'Wandering Star',
  school: 'arcane',
  description: 'Hurl a slow orb of starlight that drifts 30 yd forward over 3 s, arcing into every enemy within 4 yd of it, then bursts.',
  targeting: 'none',
  range: 30, // how far it drifts (aimed at your target when you have one)
  cooldown: { durationMs: 20_000 },
  cost: [{ resource: 'mana', amount: 500 }],
  effects: [
    {
      type: 'zone',
      zoneId: 'mage.wandering_star',
      anchor: 'caster',
      shape: { type: 'circle', radius: 4 },
      durationMs: 3000,
      affects: 'enemies',
      uniquePerCaster: true,
      motion: { type: 'linear', speed: 10, heading: 'target' },
      tick: { intervalMs: 500, effects: [{ type: 'damage', school: 'arcane', amount: { coef: 0.9, variance: 0.05 }, isAoE: true }] },
      onExpire: [{ type: 'damage', school: 'arcane', to: 'area', area: { anchor: 'point', shape: { type: 'circle', radius: 8 }, affects: 'enemies', maxTargets: 12 }, amount: { coef: 3.2, variance: 0.05 }, isAoE: true }],
    },
  ],
  ui: {
    tags: ['aoe', 'cooldown'],
    color: '#b9c6ff',
    animAs: ABILITY.stormcaller.chainLightning,
    desc: 'Hurl a slow orb of starlight toward your target (or ahead of you). It drifts 30 yd over 3 sec, arcing into every enemy within 4 yd of it for {damage} Arcane damage every 0.5 sec, then bursts for {damage2} Arcane damage to every enemy within 8 yd. Instant.',
    hint: 'Press 7 to send a drifting star through a pack: it zaps all along its path and bursts at the end.',
  },
};

export const mage: ClassDef = {
  id: BASE_CLASS.mage,
  name: 'Mage',
  role: 'dps',
  resources: [
    { id: 'mana', name: 'Mana', max: 10000, start: 10000, regenPerSec: 140, regenPerSecOoc: 600 },
    { id: 'arcana', name: 'Arcana', max: 3, start: 0 },
  ],
  binds: { '1': MA.manaDart, '2': MA.arcaneLance, '3': MA.hoarbind, '4': MA.foldStep },
  extraBinds: { '5': MA.flareBurst, '6': MA.glyphShield, '7': MA.wanderingStar },
  abilities: [manaDart, arcaneLance, hoarbind, foldStep, flareBurst, glyphShield, wanderingStar, dodge],
  extraAbilities: [ABILITY.common.dodge],
  baseStats: baseStats(),
  maxHp: PLAYER_HP.ranged,
  moveSpeed: 7,
  melee: null,
  weapon: { kind: 'staff', damage: { coef: 1.2, variance: 0.05 }, speedMs: 3600, school: 'physical' }, // +autoattack: right-click bonk in melee range
  color: '#7b6cff',
  unlocks: { [ABILITY.common.dodge]: 1, [MA.manaDart]: 1, [MA.arcaneLance]: 2, [MA.hoarbind]: 3, [MA.foldStep]: 4, [MA.flareBurst]: 5, [MA.glyphShield]: 6, [MA.wanderingStar]: 7 },
};

// ============================================================================
// Acolyte: healer. Lightlash and Dawnbrand do real damage; Tend, Aegis and Sanctum Circle heal.
// ============================================================================

const lightlash: AbilityDef = {
  id: AC.lightlash,
  name: 'Lightlash',
  school: 'holy',
  description: 'Lash an enemy with light; the most injured ally within 40 yd is mended for a little.',
  targeting: 'enemy',
  range: SPELL_RANGE,
  requiresFacing: true,
  cast: { type: 'cast', timeMs: 1500 },
  cost: [{ resource: 'mana', amount: 120 }],
  effects: [
    { type: 'damage', school: 'holy', amount: { coef: 4.5, variance: 0.05 } },
    { type: 'heal', to: 'area', area: alliesAround(40), smart: 1, amount: { coef: 1.2, variance: 0.05 } },
  ],
  ui: {
    tags: ['builder'],
    color: '#f6e27a',
    animAs: ABILITY.lightbinder.judgment,
    desc: 'Lash an enemy with light for {damage} Holy damage. The most injured ally within 40 yd (you included) is mended for {heal}.',
    hint: 'Press 1 and stand still for 1.5 s: holy damage that also mends the most hurt ally.',
  },
};

const dawnbrandDot: AuraDef = {
  id: BASE_AURA.dawnbrand,
  name: 'Dawnbrand',
  kind: 'debuff',
  durationMs: 12_000,
  stacking: 'refresh',
  pandemic: true,
  dispelType: 'magic',
  periodic: { intervalMs: 2000, effects: [{ type: 'damage', school: 'holy', amount: { coef: 0.8 } }] },
};

const dawnbrand: AbilityDef = {
  id: AC.dawnbrand,
  name: 'Dawnbrand',
  school: 'holy',
  description: 'Brand an enemy with dawnlight: it burns with holy fire for 12 s.',
  targeting: 'enemy',
  range: SPELL_RANGE,
  cost: [{ resource: 'mana', amount: 150 }],
  effects: [{ type: 'damage', school: 'holy', amount: { coef: 0.6, variance: 0.05 } }, { type: 'applyAura', aura: dawnbrandDot }],
  ui: {
    tags: ['cooldown'],
    color: '#ffd166',
    animAs: ABILITY.lightbinder.holyShock,
    desc: 'Brand an enemy for {damage} Holy damage; it burns for {damage2} every 2 sec for 12 sec. Instant.',
    hint: 'Press 2 on each enemy: an instant burn that lasts 12 s. Cast it on the move.',
  },
};

const tend: AbilityDef = {
  id: AC.tend,
  name: 'Tend',
  school: 'holy',
  description: 'A careful, steady heal on one ally.',
  targeting: 'ally',
  range: SPELL_RANGE,
  cast: { type: 'cast', timeMs: 1500 },
  cost: [{ resource: 'mana', amount: 300 }],
  effects: [{ type: 'heal', amount: { coef: 6.5, variance: 0.05 } }],
  ui: { tags: ['heal'], color: '#bff3a1', animAs: ABILITY.lightbinder.flashHeal, hint: 'Press 3 to heal your friendly target (or yourself with none).' },
};

const aegis: AbilityDef = {
  id: AC.aegis,
  name: 'Aegis',
  school: 'holy',
  description: "Shield an ally with light, absorbing damage equal to 25% of the Acolyte's maximum health for 10 s.",
  targeting: 'ally',
  range: SPELL_RANGE,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 20000 },
  cost: [{ resource: 'mana', amount: 200 }],
  effects: [{ type: 'absorb', auraId: BASE_AURA.aegis, amount: { pctSourceMaxHp: 0.25 } }],
  ui: { tags: ['externalDefensive'], color: '#ffd166', animAs: ABILITY.lightbinder.guardiansAegis, hint: 'Press 4 to shield an ally (or yourself) before the hits land.' },
};

const sanctumCircle: AbilityDef = {
  id: AC.sanctumCircle,
  name: 'Sanctum Circle',
  school: 'holy',
  description: 'Consecrate a circle for 8 s: allies inside are healed every second and enemies inside are seared.',
  targeting: 'ground',
  range: SPELL_RANGE,
  canCastWhileMoving: true,
  cooldown: { durationMs: 18000 },
  cost: [{ resource: 'mana', amount: 500 }],
  effects: [
    { type: 'zone', zoneId: 'acolyte.sanctum_circle', anchor: 'point', shape: { type: 'circle', radius: 8 }, durationMs: 8000, affects: 'allies', uniquePerCaster: true, tick: { intervalMs: 1000, effects: [{ type: 'heal', amount: { coef: 0.9 } }] } },
    { type: 'zone', zoneId: 'acolyte.sanctum_sear', anchor: 'point', shape: { type: 'circle', radius: 8 }, durationMs: 8000, affects: 'enemies', uniquePerCaster: true, tick: { intervalMs: 1000, effects: [{ type: 'damage', school: 'holy', amount: { coef: 0.7 }, isAoE: true }] } },
  ],
  ui: { tags: ['groupHeal', 'aoe'], color: '#ffe9a6', animAs: ABILITY.warden.consecratedGround, hint: 'Press 5 on the ground: heals friends and sears foes standing in it.' },
};

const hearthward: AbilityDef = {
  id: AC.hearthward,
  name: 'Hearthward',
  school: 'holy',
  description: 'Bless every ally within 40 yd with +10% maximum health for 60 minutes. It lasts through death.',
  targeting: 'none',
  cost: [{ resource: 'mana', amount: 400 }],
  effects: [
    {
      type: 'applyAura',
      to: 'area',
      area: alliesAround(40),
      aura: { id: BASE_AURA.hearthward, name: 'Hearthward', kind: 'buff', durationMs: 3_600_000, stacking: 'unique', mods: [{ stat: 'maxHpPct', value: 0.1 }], flags: { persistThroughDeath: true } },
    },
  ],
  ui: { tags: ['cooldown'], color: '#f4b860', animAs: ABILITY.lightbinder.radiantWave, desc: 'Bless every ally within 40 yd (you included) with Hearthward: +10% maximum health for 60 min. It lasts through death.', hint: 'Press 6 once an hour: +10% health for you and your party.' },
};

const dazzle: AbilityDef = {
  id: AC.dazzle,
  name: 'Dazzle',
  school: 'holy',
  description: 'A blinding flash of light stuns an enemy for 3 s.',
  targeting: 'enemy',
  range: 30,
  cooldown: { durationMs: 30_000 },
  cost: [{ resource: 'mana', amount: 150 }],
  effects: [{ type: 'applyAura', aura: { id: BASE_AURA.dazzled, name: 'Dazzled', kind: 'debuff', durationMs: 3000, stacking: 'refresh', cc: 'stun' } }],
  ui: { tags: ['cooldown'], color: '#fff3b0', animAs: ABILITY.lightbinder.holyShock, hint: 'Press 7 to stun an enemy for 3 s: stops a cast or buys a breath.' },
};

export const acolyte: ClassDef = {
  id: BASE_CLASS.acolyte,
  name: 'Acolyte',
  role: 'healer',
  resources: [{ id: 'mana', name: 'Mana', max: 10000, start: 10000, regenPerSec: 90, regenPerSecOoc: 600 }],
  binds: { '1': AC.lightlash, '2': AC.dawnbrand, '3': AC.tend, '4': AC.aegis },
  extraBinds: { '5': AC.sanctumCircle, '6': AC.hearthward, '7': AC.dazzle },
  abilities: [lightlash, dawnbrand, tend, aegis, sanctumCircle, hearthward, dazzle, dodge],
  extraAbilities: [ABILITY.common.dodge],
  baseStats: baseStats(),
  maxHp: PLAYER_HP.healer,
  moveSpeed: 7,
  melee: null,
  weapon: { kind: 'staff', damage: { coef: 1.0, variance: 0.05 }, speedMs: 2800, school: 'physical' }, // +autoattack: a mace, right-click in melee range
  color: '#f2c94c',
  unlocks: { [ABILITY.common.dodge]: 1, [AC.lightlash]: 1, [AC.dawnbrand]: 2, [AC.tend]: 3, [AC.aegis]: 4, [AC.sanctumCircle]: 5, [AC.hearthward]: 6, [AC.dazzle]: 7 },
};

// ============================================================================
// Ranger: ranged damage (bow). Aim regenerates; True Draw builds more, Longshot spends it.
// ============================================================================

const trueDraw: AbilityDef = {
  id: RA.trueDraw,
  name: 'True Draw',
  school: 'physical',
  description: 'A quick, true arrow. Usable on the move. Restores 15 Aim.',
  targeting: 'enemy',
  range: SPELL_RANGE,
  requiresFacing: true,
  gain: [{ resource: 'aim', amount: 15 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 2.7, variance: 0.05 } }],
  ui: { tags: ['builder'], color: '#c9a86a', hint: 'Press 1 to shoot, even while moving: each arrow restores Aim.' },
};

const longshot: AbilityDef = {
  id: RA.longshot,
  name: 'Longshot',
  school: 'physical',
  description: 'Spend 45 Aim: an instant power shot that hits very hard.',
  targeting: 'enemy',
  range: SPELL_RANGE + 5,
  requiresFacing: true,
  cost: [{ resource: 'aim', amount: 45 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 5.1, variance: 0.05 } }],
  ui: { tags: ['spender'], color: '#e8c27a', hint: 'Press 2 with 45 Aim for an instant heavy shot.' },
};

const snareCoil: AbilityDef = {
  id: RA.snareCoil,
  name: 'Snare Coil',
  school: 'nature',
  description: 'Throw down a coiled snare (lasts 30 s): the first enemy to step in is rooted for 4 s.',
  targeting: 'ground',
  range: 30,
  cooldown: { durationMs: 20000 },
  effects: [
    {
      type: 'zone',
      zoneId: 'ranger.snare_coil',
      anchor: 'point',
      shape: { type: 'circle', radius: 3.5 },
      durationMs: 30_000,
      affects: 'enemies',
      uniquePerCaster: true,
      triggerOnce: true,
      tick: {
        intervalMs: 250,
        effects: [
          { type: 'damage', school: 'nature', amount: { coef: 0.5 } },
          { type: 'applyAura', aura: { id: BASE_AURA.snareCoil, name: 'Snare Coil', kind: 'debuff', durationMs: 4000, stacking: 'refresh', cc: 'root' } },
        ],
      },
    },
  ],
  ui: {
    tags: ['cooldown'],
    color: '#7fb069',
    desc: 'Throw down a coiled snare up to 30 yd away. It lasts 30 sec; the first enemy to step in takes {damage} Nature damage and is rooted for 4 sec. Throw it under an enemy to root it at once.',
    hint: 'Press 3 on the ground (or under an enemy): the first foe to step in is rooted 4 s.',
  },
};

const backflip: AbilityDef = {
  id: RA.backflip,
  name: 'Backflip',
  school: 'physical',
  description: 'Flip 15 yd backwards out of trouble, still facing your target.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 20000 },
  requiresMobility: true,
  usableWhileCasting: true,
  effects: [{ type: 'dash', mode: 'backward', distance: 15, speed: 36 }],
  ui: { tags: ['defensive', 'movement'], color: '#b5c99a', hint: 'Press 4 when something reaches you: leap 15 yd back and keep shooting.' },
};

const arrowfall: AbilityDef = {
  id: RA.arrowfall,
  name: 'Arrowfall',
  school: 'physical',
  description: 'Loose a volley that rains on the target spot for 4 s, hitting every enemy within 7 yd each second.',
  targeting: 'ground',
  range: SPELL_RANGE,
  canCastWhileMoving: true,
  cooldown: { durationMs: 10000 },
  cost: [{ resource: 'aim', amount: 30 }],
  effects: [{ type: 'zone', zoneId: 'ranger.arrowfall', anchor: 'point', shape: { type: 'circle', radius: 7 }, durationMs: 4000, affects: 'enemies', uniquePerCaster: true, tick: { intervalMs: 1000, effects: [{ type: 'damage', school: 'physical', amount: { coef: 1.5 }, isAoE: true }] } }],
  ui: { tags: ['aoe'], color: '#d8b46a', hint: 'Press 5 on a pack: arrows rain on everything within 7 yd for 4 s.' },
};

const swiftfootBuff: AuraDef = {
  id: BASE_AURA.swiftfoot,
  name: 'Swiftfoot',
  kind: 'buff',
  durationMs: 0,
  stacking: 'refresh',
  mods: [{ stat: 'moveSpeedPct', value: 0.3 }],
  flags: { removeInCombat: true },
};

const swiftfoot: AbilityDef = {
  id: RA.swiftfoot,
  name: 'Swiftfoot',
  school: 'nature',
  description: 'Out of combat: run 30% faster until you press it again or a fight starts.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 1000 },
  usableIf: { type: 'or', any: [OUT_OF_COMBAT, { type: 'selfHasAura', auraId: BASE_AURA.swiftfoot }] },
  effects: [toggle(swiftfootBuff)],
  ui: { tags: ['movement'], color: '#a8d672', desc: 'Out of combat only. Toggle: run 30% faster until you press it again or a fight starts.', hint: 'Press 6 between fights to run 30% faster; it drops when combat starts.' },
};

const pinShot: AbilityDef = {
  id: RA.pinShot,
  name: 'Pin Shot',
  school: 'physical',
  description: "A sharp shot to the caster's hand, interrupting its cast and locking that school for 3 s.",
  targeting: 'enemy',
  range: SPELL_RANGE,
  requiresFacing: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15000 },
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.5 } }, interrupt(3000)],
  ui: { tags: ['interrupt'], color: '#e0b25a', hint: 'Press 7 while an enemy is casting to stop the spell from range.' },
};

export const ranger: ClassDef = {
  id: BASE_CLASS.ranger,
  name: 'Ranger',
  role: 'dps',
  // +autoattack: Auto Shot hits restore a little Aim.
  resources: [{ id: 'aim', name: 'Aim', max: 100, start: 100, regenPerSec: 8, gainOn: [{ on: 'meleeSwingLanded', amount: 3 }] }],
  binds: { '1': RA.trueDraw, '2': RA.longshot, '3': RA.snareCoil, '4': RA.backflip },
  extraBinds: { '5': RA.arrowfall, '6': RA.swiftfoot, '7': RA.pinShot },
  abilities: [trueDraw, longshot, snareCoil, backflip, arrowfall, swiftfoot, pinShot, dodge],
  extraAbilities: [ABILITY.common.dodge],
  baseStats: baseStats(),
  maxHp: PLAYER_HP.ranged,
  moveSpeed: 7,
  melee: null,
  weapon: { kind: 'ranged', damage: { coef: 1.9, variance: 0.05 }, speedMs: 3000, school: 'physical', range: 40 }, // +autoattack: Auto Shot (bow)
  color: '#7fb069',
  unlocks: { [ABILITY.common.dodge]: 1, [RA.trueDraw]: 1, [RA.longshot]: 2, [RA.snareCoil]: 3, [RA.backflip]: 4, [RA.arrowfall]: 5, [RA.swiftfoot]: 6, [RA.pinShot]: 7 },
};

/** The five playable base classes, in Group Finder slot order (tank, healer, then damage). */
export const BASE_CLASSES: ClassDef[] = [warrior, acolyte, rogue, mage, ranger];

/** Standalone auras the base kits reference by id (absorb shields; auras granted only by procs or removed by id). */
export const BASE_AURAS: AuraDef[] = [
  { id: BASE_AURA.glyphShield, name: 'Glyph Shield', kind: 'buff', durationMs: 10000, stacking: 'refresh', absorb: {} },
  { id: BASE_AURA.aegis, name: 'Aegis', kind: 'buff', durationMs: 10000, stacking: 'refresh', absorb: {} },
  freshKill,
  lurkBuff,
  swiftfootBuff,
];

/** Old class whose art, crest, model and anim kit a base reuses (Ranger borrows the Stormcaller's caster frame). */
export const BASE_CLASS_ART: Readonly<Record<string, string>> = {
  warrior: 'warden',
  acolyte: 'lightbinder',
  rogue: 'blademaster',
  mage: 'stormcaller',
  ranger: 'stormcaller',
};

/** New ability id → old ability whose icon / VFX / clip it reuses (from `ui.animAs`). */
export const BASE_VISUAL_ALIAS: Readonly<Record<string, string>> = Object.fromEntries(
  BASE_CLASSES.flatMap((c) => c.abilities.flatMap((a) => (a.ui?.animAs ? [[a.id, a.ui.animAs]] : []))),
);

/** Level-10 specialisation preview (character creation "Evolves into"; not playable yet). */
export interface SpecPreview {
  name: string;
  role: 'tank' | 'healer' | 'dps';
  blurb: string;
  /** The recommended launch set (docs/design/classes/advancement.md, bold). */
  launch: boolean;
}

export const BASE_SPECS: Readonly<Record<string, readonly SpecPreview[]>> = {
  warrior: [
    { name: 'Ironclad', role: 'tank', blurb: 'Iron plates and shrapnel: an unbreakable wall that bites back.', launch: true },
    { name: 'Ravager', role: 'dps', blurb: 'Two greatswords and an Overdrive that never wants to stop.', launch: true },
    { name: 'Bloodreaver', role: 'dps', blurb: 'Bleeds itself for power, strongest on the edge of death.', launch: false },
  ],
  rogue: [
    { name: 'Shadowdancer', role: 'dps', blurb: 'Strikes from everywhere at once with shadow clones.', launch: true },
    { name: 'Mirage', role: 'tank', blurb: 'An evasion tank who leaves afterimages to take the hits.', launch: true },
    { name: 'Venomwright', role: 'dps', blurb: 'A toxin alchemist who brews death over time.', launch: false },
  ],
  mage: [
    { name: 'Stormcaller', role: 'dps', blurb: 'Overvolt lightning: build the charge, then let the sky fall.', launch: true },
    { name: 'Rimecaller', role: 'dps', blurb: 'Freeze the battlefield, then fracture it.', launch: true },
    { name: 'Cinderborn', role: 'dps', blurb: 'Burns its own life for fire, and rises from the ash.', launch: false },
  ],
  acolyte: [
    { name: 'Lightbinder', role: 'healer', blurb: 'Wards and Absolution: the classic light healer.', launch: true },
    { name: 'Heretic', role: 'dps', blurb: 'Curses, tithes and a summoned horror.', launch: true },
    { name: 'Hemomancer', role: 'healer', blurb: 'A healer who bleeds to heal.', launch: false },
  ],
  ranger: [
    // +ranger-specs: display names from RANGER_SPEC_NAME (specs/ranger.ts), the one place to rename them.
    { name: RANGER_SPEC_NAME.deadeye, role: 'dps', blurb: 'A gunslinger with a six-shot cylinder and the perfect reload.', launch: true },
    { name: RANGER_SPEC_NAME.grovewarden, role: 'healer', blurb: 'Heals over time with the patience of the forest.', launch: true },
    { name: RANGER_SPEC_NAME.beastbound, role: 'dps', blurb: 'Bonded to a spirit beast that evolves with you.', launch: false },
  ],
};
